import React, { useEffect, useState, useContext } from 'react';

import { PageContext } from '../components/page';
import PageContentWrapper from '../components/pageContentWrapper';
import { CreateTripGroupForm } from './CreateTripGroupForm';
import { EditTripGroupPath } from '../../appRoutes';
import {
  createTripGroup,
  getTripGroupAttributes,
} from '../../service/tripGroupService';
import { CreateAttribute } from '../../types/tripGroup';
import { useNavigate } from 'react-router-dom';

export type InputType = CreateAttribute & { type: 'BOOLEAN' | 'STRING' };

const CreateTripGroupContainer: React.FC = () => {
  const { pushAlert } = useContext(PageContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [attributes, setAttributes] = useState<InputType[]>([
    { name: '', value: '', type: 'STRING' },
  ]);

  const onChange = (field: string, value: string) => {
    const nextAttributes = attributes.map(a => {
      return { ...a, value: a.name === field ? value : a.value };
    });
    setAttributes(nextAttributes);
  };

  const onSave = async (): Promise<void> => {
    if (!name.trim()) {
      pushAlert('danger', 'Name is required.');
      return;
    }

    setLoading(true);
    const data = {
      name: name.trim(),
      attributes: attributes.map(a => {
        return { name: a.name, value: a.value };
      }),
    };

    try {
      const result = await createTripGroup(data);
      if (result.created) {
        navigate(
          EditTripGroupPath.replace(
            ':tripGroupId',
            result.data.tripGroupView.id,
          ),
        );
      } else {
        pushAlert('danger', 'Trip Group already exists.');
        setLoading(false);
      }
    } catch (err) {
      if (err instanceof Error) {
        pushAlert('danger', `Error saving trip group. ${err.message}`);
      } else {
        pushAlert('danger', 'An unknown error occurred.');
      }
      setLoading(false);
    }
  };

  const onClear = () => {
    const nextAttributes = attributes.map(a => {
      return { ...a, value: '' };
    });
    setAttributes(nextAttributes);
    setName('');
  };

  useEffect(() => {
    const fetchAttributes = async () => {
      setLoading(true);
      try {
        const result = await getTripGroupAttributes();
        const attr = result.accountAttributes
          .map(attr => {
            return { name: attr.name, value: '', type: attr.type };
          })
          .filter(a => a.name !== 'ACCOUNT_ID');
        setAttributes(attr);
      } catch (err) {
        if (err instanceof Error) {
          pushAlert('danger', `Error fetching trip group. ${err.message}`);
        } else {
          pushAlert('danger', 'An unknown error occurred while fetching trip group attribute values.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, []);

  return (
    <PageContentWrapper title="CREATE TRIP GROUP" loading={loading}>
      {attributes?.length ? (
        <CreateTripGroupForm
          name={name}
          setName={setName}
          attributes={attributes}
          onChange={onChange}
          onClear={onClear}
          onSave={onSave}
        />
      ) : (
        'Error loading account attributes. Please refresh and try again or contact support if this is unexpected.'
      )}
    </PageContentWrapper>
  );
};
export default CreateTripGroupContainer;
